import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "context/AuthContext";
import { callChatUnreadCount } from "api/MuyeonApi";
import { hasNativeBridge } from "util/userAgent";
import { openChatList } from "common/nativeBridge";
import { onChatRoomListChange } from "common/appSocket";
import { openNativeOrWeb } from "common/nativeRoute";
import { chatFilterForActive } from "constants/permissions";
import { trackClick } from "util/appAnalytics";
import { emitTabReselect } from "hooks/useTabReselect";
import TutorialOverlay from "components/muyeon/TutorialOverlay";
import TypeNewsBubble from "components/muyeon/TypeNewsBubble";
import ActivityPulseDot from "components/ui/ActivityPulseDot";
import useTypeBadges from "hooks/useTypeBadges";
import { TUTORIAL_KEYS, hasSeenTutorial, markTutorialSeen, syncTutorialFlagsFromServer } from "util/tutorialFlags";
import ActiveTypeSheet, { TYPE_TAB_LABEL, visibleType, switchableTypes, openNativeTypeSheet } from "components/muyeon/ActiveTypeSheet";

const LONG_PRESS_MS = 500;   // MY 탭 길게 누르기 → 유형 시트
const DOUBLE_TAP_MS = 350;   // MY 탭 두 번 누르기 → 반대 유형(유형이 2개일 때)

// 무용연 하단 탭 5개 + 플로팅(채팅/상단이동)
const Footer = ({ selectedFooterMenu, setSelectedFooterMenu, tabs }) => {
    const navigator = useNavigate();
    const location = useLocation();
    const { isLoggedIn, activeType, user, setActiveType } = useAuth();
    const isNative = hasNativeBridge();

    const [showTop, setShowTop] = useState(false); // 스크롤 내려가면 상단이동 버튼 노출
    const [unread, setUnread] = useState(0);        // 채팅 안읽음 합계(뱃지)
    // 탭 목록은 레이아웃이 받아 내려 준다(useTabMenus) — 레이아웃도 '지금 화면이 탭인가'를 같은 목록으로 판단한다.
    const BOTTOM_LIST = tabs;

    /* 현재 path로 선택 버튼 활성화 (하위 경로 포함) */
    useEffect(() => {
        const current = BOTTOM_LIST.find((item) => location.pathname.startsWith(item.path));
        if (current) setSelectedFooterMenu(current.class);
        // 서버 탭은 마운트 뒤에 도착한다 — 빼면 첫 진입에서 선택 표시가 안 붙는다.
    }, [location.pathname, tabs]); // eslint-disable-line react-hooks/exhaustive-deps

    /* 스크롤 위치 → 상단이동 버튼 토글. 최상단이면 숨김(그 자리에 채팅버튼이 내려감)
       capture:true 로 window/내부 컨테이너(.content-wrap 등) 스크롤 모두 감지 */
    useEffect(() => {
        const onScroll = (e) => {
            const y =
                (e && e.target && typeof e.target.scrollTop === "number" ? e.target.scrollTop : 0) ||
                window.scrollY ||
                document.documentElement.scrollTop ||
                0;
            setShowTop(y > 200);
        };
        window.addEventListener("scroll", onScroll, { passive: true, capture: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll, { capture: true });
    }, []);

    /* 채팅 안읽음 수 — 웹 플로팅 버튼용. 실시간 신호(room-updated·chat-room-added·재연결)와
       폴링·화면 복귀·경로 변경 시 갱신하되, 조회는 800ms 디바운스로 묶어 신호마다 호출하지 않는다.
       네이티브는 자체 FloatingOverlay가 배지를 관리하므로 웹에서 중복 조회하지 않는다. */
    const unreadRefreshRef = useRef(() => {});
    useEffect(() => {
        if (!isLoggedIn || isNative) { setUnread(0); unreadRefreshRef.current = () => {}; return; }
        let alive = true;
        let timer = null;
        let inFlight = false;
        const fetchUnread = () => {
            if (inFlight || document.visibilityState === "hidden") return;
            inFlight = true;
            callChatUnreadCount()
                .then((r) => { if (alive) setUnread(Number(r?.data?.count) || 0); })
                .catch(() => {})
                .finally(() => { inFlight = false; });
        };
        const schedule = () => {
            clearTimeout(timer);
            timer = setTimeout(fetchUnread, 800);
        };
        unreadRefreshRef.current = schedule;
        fetchUnread();
        const poll = setInterval(schedule, 30000);
        const onVis = () => { if (document.visibilityState === "visible") schedule(); };
        document.addEventListener("visibilitychange", onVis);
        const off = onChatRoomListChange({ onAdded: schedule, onUpdated: schedule, onReconnect: schedule });
        return () => {
            alive = false;
            clearTimeout(timer);
            clearInterval(poll);
            off();
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [isLoggedIn, isNative]);
    // 경로 변경(예: 채팅방에서 나와 읽음 반영) 시에도 같은 디바운스로 갱신한다.
    //  첫 렌더는 위 효과가 이미 조회하므로 건너뛴다.
    const pathSeenRef = useRef(false);
    useEffect(() => {
        if (!pathSeenRef.current) { pathSeenRef.current = true; return; }
        unreadRefreshRef.current();
    }, [location.pathname]);

    /* ★ 2026-10-31 MY 탭 = 내 유형(ActiveTypeSheet 설명 참고). 로그인 전에는 예전 그대로 'MY'. */
    const [typeSheetOpen, setTypeSheetOpen] = useState(false);
    const pressTimer = useRef(null);
    const longPressed = useRef(false);
    const lastMyTap = useRef(0);
    const currentType = visibleType(activeType);
    // ★ 2026-10-31 지금 유형 새 소식(빨간 점) · 다른 유형 새 소식(말풍선·시트 줄 점) — hooks/useTypeBadges.
    const typeBadges = useTypeBadges(isLoggedIn, currentType);
    const newTypes = Object.keys(typeBadges.others);
    const openTypeSheet = () => {
        if (!openNativeTypeSheet(switchableTypes(user), currentType, newTypes)) setTypeSheetOpen(true);
    };
    // 길게 누르기는 앱에서만 — 브라우저(특히 아이폰 사파리)는 링크를 길게 누르면 미리보기·'새 탭에서 열기'가 뜬다.
    //  브라우저는 MY 화면에서 MY 탭을 한 번 더 누르면 시트를 연다(handleMyTap).
    const myPressHandlers = !isLoggedIn || !isNative ? {} : {
        onPointerDown: () => {
            longPressed.current = false;
            clearTimeout(pressTimer.current);
            pressTimer.current = setTimeout(() => {
                longPressed.current = true;
                try { window.navigator?.vibrate?.(10); } catch (e) { /* 진동 없는 기기 */ }
                openTypeSheet();
            }, LONG_PRESS_MS);
        },
        onPointerUp: () => clearTimeout(pressTimer.current),
        onPointerLeave: () => clearTimeout(pressTimer.current),
        onPointerCancel: () => clearTimeout(pressTimer.current),
        // 길게 누를 때 뜨는 링크 미리보기·복사 메뉴를 막는다.
        onContextMenu: (e) => e.preventDefault(),
        style: { WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none" },
    };
    /* ★ 2026-10-31 가입 직후 홈 첫 진입 1회 — MY(내 유형) 탭을 비추고 안내(강사 탭 튜토리얼과 같은 오버레이).
       신규 회원(가입 14일 안)만 — 서버가 준 createdAt 기준(없으면 띄우지 않는다).
       가입 직후 다른 안내 팝업(유형 인증·승인 등)이 떠 있으면 그게 닫힐 때까지 기다린다(최대 20초, 못 띄우면 다음 홈 진입 때).
       본 기록은 계정(uiFlags)+기기에 남아 다시 뜨지 않는다. 비춘 탭을 누르면 안내를 닫고 바로 유형 시트를 연다. */
    const [myTipOpen, setMyTipOpen] = useState(false);
    const createdMs = user?.createdAt ? Date.parse(user.createdAt) : NaN;
    const isNewMember = Number.isFinite(createdMs) && Date.now() - createdMs < 14 * 24 * 60 * 60 * 1000;
    useEffect(() => {
        if (!isLoggedIn || !isNewMember || location.pathname !== "/home") return;
        let alive = true;
        let timer = null;
        let tries = 0;
        const modalOpen = () => !!document.querySelector(".modal-dimmed.is-show, .modal-bottom-dimmed.is-show");
        const attempt = () => {
            if (!alive) return;
            if (hasSeenTutorial(TUTORIAL_KEYS.myTypeTab)) return;
            if (!document.querySelector('[data-tut="my-tab"]') || modalOpen()) {
                if (++tries < 20) timer = setTimeout(attempt, 1000);
                return;
            }
            setMyTipOpen(true);
        };
        syncTutorialFlagsFromServer().finally(() => { timer = setTimeout(attempt, 1200); });
        return () => { alive = false; clearTimeout(timer); };
    }, [isLoggedIn, isNewMember, location.pathname]);
    const closeMyTip = () => { setMyTipOpen(false); markTutorialSeen(TUTORIAL_KEYS.myTypeTab); };

    /** MY 탭 누름 처리 — 처리했으면 true.
     *  앱: 길게 누른 뒤 손 뗌은 무시, 빠르게 두 번 누르면 반대 유형(유형이 2개일 때).
     *  브라우저: MY 화면에서 한 번 더 누르면 유형 시트. */
    const handleMyTap = (item) => {
        if (!isLoggedIn) return false;
        if (!isNative) {
            if (location.pathname.startsWith(item.path)) { openTypeSheet(); return true; }
            return false;
        }
        if (longPressed.current) { longPressed.current = false; return true; }
        const now = Date.now();
        const types = switchableTypes(user);
        if (now - lastMyTap.current < DOUBLE_TAP_MS && types.length === 2) {
            lastMyTap.current = 0;
            setActiveType(types.find((t) => t !== currentType) || types[0]);
            return true;
        }
        lastMyTap.current = now;
        return false;
    };

    const handleScrollTop = useCallback(() => window.scrollTo({ top: 0, behavior: "smooth" }), []);
    const handleChat = useCallback(() => {
        // 앱=네이티브 채팅 리스트(활성유형 세그먼트), 웹=/chat
        openNativeOrWeb(openChatList, { filter: chatFilterForActive(activeType) }, navigator);
    }, [activeType, navigator]);

    return (
        <>
        <div className="tab-bar-nav fixed">
            <div className="nav-box">
                <nav>
                    {BOTTOM_LIST.map((item) => (
                        <a
                            key={item.class}
                            href="#"
                            className={"nav-btn" + (selectedFooterMenu === item.class ? " selected" : "")}
                            {...(item.class === "my" ? {
                                ...myPressHandlers,
                                "data-tut": "my-tab",
                                // 빨간 점·말풍선을 이 탭 기준으로 띄운다.
                                style: { ...(myPressHandlers.style || {}), position: "relative" },
                            } : {})}
                            onClick={(e) => {
                                e.preventDefault();
                                if (item.class === "my" && handleMyTap(item)) return;
                                trackClick(`tab:${item.class}`);
                                // ★ 10-26 앱의 '캐스팅·무대' 탭은 네이티브 화면으로 연다(능력 표시 없는 옛 앱·브라우저는 웹).
                                //   화면이 열릴 때 넘기면 되풀이 위험이 있어 탭 누름에서만 넘긴다.
                                if (item.path === "/casting" && isNative) {
                                    openNativeOrWeb("openCastingStage", {}, navigator);
                                    return;
                                }
                                // 보고 있는 탭을 다시 누르면 다음 칩으로(useTabReselect). 받는 화면이 없으면 맨 위로.
                                if (location.pathname === item.path) {
                                    if (!emitTabReselect(item.path)) window.scrollTo({ top: 0, behavior: "smooth" });
                                    return;
                                }
                                navigator(item.path);
                            }}
                        >
                            <i className={item.icon}></i>
                            {item.class === "my" && isLoggedIn ? (TYPE_TAB_LABEL[currentType] || item.title) : item.title}
                            {item.class === "my" && isLoggedIn && typeBadges.mine > 0 && (
                                <span style={{ position: "absolute", top: "0.2rem", right: "0.8rem", lineHeight: 0, pointerEvents: "none" }}>
                                    <ActivityPulseDot label="새 소식 있음" />
                                </span>
                            )}
                            {item.class === "my" && isLoggedIn && (
                                <TypeNewsBubble others={typeBadges.others} isNative={isNative} blocked={myTipOpen || typeSheetOpen} />
                            )}
                        </a>
                    ))}
                </nav>

                {/* 플로팅: 채팅버튼(항상, 위) + 상단이동(스크롤시, 아래).
                    floating-wrap 은 bottom 고정이라 상단이동이 사라지면 채팅버튼이 그 자리로 내려감. */}
                <div className="floating-wrap">
                    {/* 네이티브는 자체 FloatingOverlay를 사용하고, 브라우저에서만 웹 채팅 버튼을 표시한다. */}
                    {isLoggedIn && !isNative && (
                        <button
                            type="button"
                            className="floating-btn chat"
                            onClick={handleChat}
                            style={{
                                position: "relative", overflow: "visible",
                                width: "4.8rem", height: "4.8rem",
                                borderRadius: "50%", background: "#F58232",
                            }}
                        >
                            {/* iOS FloatingOverlay: 48px 주황 원 + 18px 흰색 이중 말풍선과 동일 규격. */}
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path
                                    d="M8 3.5h9.5A3.5 3.5 0 0 1 21 7v5a3.5 3.5 0 0 1-3.5 3.5H14L10.5 19v-3.5H8A3.5 3.5 0 0 1 4.5 12V7A3.5 3.5 0 0 1 8 3.5Z"
                                    fill="#fff"
                                />
                                <path
                                    d="M4.5 8.5H4A3 3 0 0 0 1 11.5v4A3 3 0 0 0 4 18.5h1.5L8.5 21v-2.5H11a3 3 0 0 0 2.83-2H8A4.5 4.5 0 0 1 3.5 12V9.67c.3-.47.63-.86 1-1.17Z"
                                    fill="#fff"
                                    opacity="0.92"
                                />
                            </svg>
                            {unread > 0 && (
                                <span
                                    style={{
                                        position: "absolute",
                                        top: "-0.3rem",
                                        right: "-0.3rem",
                                        minWidth: "1.7rem",
                                        height: "1.7rem",
                                        padding: "0 0.4rem",
                                        background: "#e2362d",
                                        color: "#fff",
                                        borderRadius: "99rem",
                                        fontSize: "1rem",
                                        lineHeight: "1.7rem",
                                        textAlign: "center",
                                        fontWeight: 700,
                                        boxSizing: "border-box",
                                    }}
                                >
                                    {unread > 99 ? "99+" : unread}
                                </span>
                            )}
                            <span className="visually-hidden">채팅</span>
                        </button>
                    )}

                    {showTop && (
                        <button type="button" className="floating-btn move-top" onClick={handleScrollTop}>
                            <i className="top"></i>
                            <span className="visually-hidden">상단바로가기</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
        {/* 시트는 탭바 밖에 둔다 — 탭바(fixed) 안이면 시트의 position:fixed 가 탭바 기준으로 깨질 수 있다. */}
        {isLoggedIn && <ActiveTypeSheet isShow={typeSheetOpen} onClose={() => setTypeSheetOpen(false)} newTypes={newTypes} />}
        <TutorialOverlay open={myTipOpen} onClose={closeMyTip}
            onTargetClick={() => { closeMyTip(); openTypeSheet(); }}
            steps={[{ target: '[data-tut="my-tab"]', text: isNative
                ? "여기를 길게 눌러 보세요! 강사·학원 등 내 유형을 바로 바꿀 수 있어요."
                : "MY 화면에서 여기를 한 번 더 누르면 강사·학원 등 내 유형을 바로 바꿀 수 있어요." }]} />
        </>
    );
};

export default Footer;
